import { createContext, useContext, type ReactNode } from "react";

/**
 * Permissões do usuário logado, vindas de GET /usuarios/me.
 *
 * Formato: permissoes[condominioId][funcionalidade] = { ler, escrever }.
 * O Dono (is_dono) enxerga e faz tudo, então os helpers já respondem `true`
 * para ele sem consultar o mapa.
 *
 * Serve para ESCONDER botões de escrita para papéis somente-leitura — o backend
 * já bloqueia de qualquer forma (a tabela de permissões é a fonte da verdade),
 * mas assim o usuário não vê ações que não pode concluir.
 */
export type PermsMap = Record<number, Record<string, { ler: boolean; escrever: boolean }>>;

interface Ctx {
  isDono: boolean;
  permissoes: PermsMap;
  ehGestor: boolean;       // Dono OU síndico de algum condomínio
  sindicoEm: number[];     // ids dos condomínios onde é síndico
}

const PermCtx = createContext<Ctx>({ isDono: false, permissoes: {}, ehGestor: false, sindicoEm: [] });

export function PermissoesProvider({
  isDono,
  permissoes,
  ehGestor = false,
  sindicoEm = [],
  children,
}: {
  isDono: boolean;
  permissoes: PermsMap;
  ehGestor?: boolean;
  sindicoEm?: number[];
  children: ReactNode;
}) {
  return (
    <PermCtx.Provider value={{ isDono, permissoes, ehGestor, sindicoEm }}>
      {children}
    </PermCtx.Provider>
  );
}

export function usePermissoes() {
  const { isDono, permissoes, ehGestor, sindicoEm } = useContext(PermCtx);
  return {
    isDono,
    /** Dono ou síndico de algum condomínio (libera áreas de gestão). */
    ehGestor,
    /** É síndico DESTE condomínio? (ou Dono) */
    ehSindico: (condominioId: number): boolean => isDono || sindicoEm.includes(condominioId),
    /** Pode gerir a EQUIPE deste condomínio (cadastrar usuários/permissões)? */
    podeGerirEquipe: (condominioId: number): boolean => isDono || sindicoEm.includes(condominioId),
    /** Pode ESCREVER (criar/editar/excluir) nesta funcionalidade do condomínio? */
    podeEscrever: (condominioId: number, funcionalidade: string): boolean =>
      isDono || !!permissoes[condominioId]?.[funcionalidade]?.escrever,
    /** Pode LER esta funcionalidade do condomínio? */
    podeLer: (condominioId: number, funcionalidade: string): boolean =>
      isDono || !!permissoes[condominioId]?.[funcionalidade]?.ler,
    /** Pode LER esta funcionalidade em ALGUM condomínio (p/ menus gerais)? */
    podeLerAlgum: (funcionalidade: string): boolean =>
      isDono || Object.values(permissoes).some((m) => m?.[funcionalidade]?.ler),
  };
}
